/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { AssessmentRowSheet } from '@/app/(client)/slcp/-components/AssessmentRowSheet';
import { SlcpAssessmentTable } from '@/app/(client)/slcp/-components/SlcpAssessmentTable';
import { useAssessmentRowSelection } from '@/app/(client)/slcp/-hooks/useAssessmentRowSelection';
import { useSlcpAssessmentTabPage } from '@/app/(client)/slcp/-hooks/useSlcpAssessmentTabPage';

const searchSchema = z.object({
  /** External id of the question whose detail sheet should be open. */
  question: z.string().optional(),
});

export const Route = createFileRoute(
  '/(client)/slcp/(pages)/assessments/(details)/$assessmentId/assessment/',
)({
  validateSearch: searchSchema,
  component: SlcpAssessmentTabPage,
});

function SlcpAssessmentTabPage() {
  const { assessmentId } = Route.useParams();
  const search = Route.useSearch();
  const {
    t,
    isLoading,
    structure,
    counts,
    sectionOptions,
    subsectionOptions,
    filters,
    setView,
    setSearch,
    setSections,
    setSubsections,
    expanded,
    setExpanded,
    isAllExpanded,
    toggleExpandAll,
  } = useSlcpAssessmentTabPage(assessmentId);

  const { selectedRow, openRow, closeRow } = useAssessmentRowSelection({
    structure,
    questionId: search.question,
    fromPath: Route.fullPath,
  });

  if (isLoading) {
    return (
      <div className='flex flex-col gap-4 animate-pulse'>
        <div className='h-10 rounded-lg bg-muted' />
        <div className='h-96 rounded-lg bg-muted' />
      </div>
    );
  }

  return (
    // Bounded height so the grouped DataTable fills + scrolls internally, while
    // the shared page header above it scrolls away with the page.
    <div className='flex flex-col gap-4 h-[calc(100dvh-15rem)] min-h-[24rem]'>
      <SlcpAssessmentTable
        sections={structure}
        expanded={expanded}
        onExpandedChange={setExpanded}
        view={filters.view}
        onViewChange={setView}
        counts={counts}
        searchValue={filters.search}
        selectedSections={filters.sections}
        selectedSubsections={filters.subsections}
        onSearchChange={setSearch}
        onSectionsChange={setSections}
        onSubsectionsChange={setSubsections}
        sectionOptions={sectionOptions}
        subsectionOptions={subsectionOptions}
        isAllExpanded={isAllExpanded}
        onToggleExpandAll={toggleExpandAll}
        onRowClick={openRow}
        t={t}
      />

      <AssessmentRowSheet
        open={!!selectedRow}
        onClose={closeRow}
        assessmentId={assessmentId}
        row={selectedRow}
        t={t}
      />
    </div>
  );
}
